import { Form, Table } from 'react-bootstrap';
import { MdAdd, MdDeleteForever } from 'react-icons/md';
import { GlobalContent } from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { selectOptions } from '@Components/EntityOverviewForm/entity-overview-form-utils';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { useExperimentFlag } from '@src/utils/experiment-context';
import {
  CONTENT_PLATFORM_PREFIX_OPTIONS,
  indexedVisibleRows,
  StreamingNetworkRecord,
  updateAt
} from './streaming-networks-constants';

// CONTENT_PLATFORM_PREFIX_OPTIONS is typed as SelectorOption[] (label/value: string | number) since
// it comes from the shared toOptions() helper — Dropdown wants string-only options, so this narrows
// via the same stringify EntityOverviewForm uses for its own SELECT fields.
const PLATFORM_PREFIX_OPTIONS = selectOptions(CONTENT_PLATFORM_PREFIX_OPTIONS);

interface NetworkContentTabProps {
  network: StreamingNetworkRecord;
  editing: boolean;
  onFieldChange: <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => void;
}

export const NetworkContentTab = ({ network, editing, onFieldChange }: NetworkContentTabProps) => {
  // Adding/removing content values is gated separately from the page's shared `editing` flag — only
  // users with this flag on can grow or shrink the list; anyone in edit mode can still fix a typo in
  // an existing row's value/prefix.
  const canManageContent = Boolean(useExperimentFlag('enableStreamingNetworksUpdates'));
  const { globalContent } = network;

  const setGlobalContent = (updated: GlobalContent[]) => onFieldChange('globalContent', updated);

  const updateContent = (originalIndex: number, updated: GlobalContent) =>
    setGlobalContent(updateAt(globalContent, originalIndex, updated));

  const removeContent = (originalIndex: number) =>
    updateContent(originalIndex, { ...globalContent[originalIndex], deleted: true });

  const addContent = () =>
    setGlobalContent([
      ...globalContent,
      { contentValue: '', platformPrefixValue: '', deleted: false }
    ]);

  const visibleRows = indexedVisibleRows(globalContent).sort((a, b) => {
    const prefixCompare = a.row.platformPrefixValue.localeCompare(b.row.platformPrefixValue);
    return prefixCompare !== 0
      ? prefixCompare
      : a.row.contentValue.localeCompare(b.row.contentValue);
  });

  if (visibleRows.length === 0 && !(canManageContent && editing)) {
    return <div className='sn-empty'>There are currently no content values for this network.</div>;
  }

  return (
    <div className='sn-content'>
      <Table size='sm' className='sn-content-table'>
        <thead>
          <tr>
            <th>Content Value</th>
            <th>Platform Prefix</th>
            {canManageContent && editing && <th aria-label='Remove' />}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ row: content, originalIndex }) => (
            <tr key={originalIndex}>
              <td>
                <Form.Control
                  size='sm'
                  disabled={!editing}
                  value={content.contentValue}
                  onChange={event =>
                    updateContent(originalIndex, {
                      ...content,
                      originalContentValue: content.originalContentValue ?? content.contentValue,
                      contentValue: event.target.value
                    })
                  }
                />
              </td>
              <td>
                <div className='sn-content-prefix-dropdown'>
                  <Dropdown<string>
                    options={PLATFORM_PREFIX_OPTIONS}
                    value={content.platformPrefixValue}
                    disabled={!editing}
                    placeholder='Select…'
                    onChange={value =>
                      updateContent(originalIndex, {
                        ...content,
                        originalPlatformPrefixValue:
                          content.originalPlatformPrefixValue ?? content.platformPrefixValue,
                        platformPrefixValue: value
                      })
                    }
                  />
                </div>
              </td>
              {canManageContent && editing && (
                <td>
                  <IconButton
                    size={Size.MD}
                    onClick={() => removeContent(originalIndex)}
                    aria-label='Remove content value'
                  >
                    <MdDeleteForever />
                  </IconButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {canManageContent && editing && (
        <Button className='sn-add-action' leadingElement={<MdAdd />} onClick={addContent}>
          Add content value
        </Button>
      )}
    </div>
  );
};
